'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var getIndexedDB = function getIndexedDB() {
  if (typeof window.indexedDB !== 'undefined') {
    return window.indexedDB;
  }
  if (typeof window.webkitIndexedDB !== 'undefined') {
    return window.webkitIndexedDB;
  }
  if (typeof window.mozIndexedDB !== 'undefined') {
    return window.mozIndexedDB;
  }
  if (typeof window.OIndexedDB !== 'undefined') {
    return window.OIndexedDB;
  }
  if (typeof window.msIndexedDB !== 'undefined') {
    return window.msIndexedDB;
  }

  throw new Error('Unable to find IndexedDB');
};

var READ_ONLY = 'readonly';
var READ_WRITE = 'readwrite';
var DEFAULT_VERSION = 1;
var DEFAULT_DB_NAME = 'AsyncStorage';

var clearStore = function clearStore(dbInstance, name) {
  return new Promise(function (resolve, reject) {
    try {
      var transaction = dbInstance.transaction(name, READ_WRITE);
      var objectStore = transaction.objectStore(name);

      var req = objectStore.clear();

      var onError = function onError() {
        var err = req.error ? req.error : req.transaction.error;
        reject(err);
      };

      transaction.oncomplete = function () {
        return resolve();
      };
      transaction.onabort = onError;
      transaction.onerror = onError;
    } catch (err) {
      reject({
        storeName: name,
        error: err
      });
    }
  });
};

var defaultOptions = {
  autoIncrement: true,
  disableContexts: false // Allow the ability to disable contexts for testing
};

var indexedDB$1 = getIndexedDB();

var dbContexts = {};

var isUpgradeRequired = function isUpgradeRequired(dbInfo) {
  if (!dbInfo.db) {
    return true;
  }

  var isNewStore = !dbInfo.db.objectStoreNames.contains(dbInfo.storeName);
  var isUpgrade = dbInfo.version > dbInfo.db.version;
  var isDowngrade = dbInfo.version < dbInfo.db.version;

  if (isNewStore) {
    var nextVersion = dbInfo.db.version + 1;

    if (nextVersion > dbInfo.version) {
      dbInfo.version = nextVersion;
    }

    return true;
  }

  if (isUpgrade) {
    return true;
  }

  // Downgrades are not possible, align the versions to avoid errors
  if (isDowngrade) {
    if (dbInfo.version !== DEFAULT_VERSION) {
      console.warn('Database ' + dbInfo.name + ' has been downgraded. Aligning versions to avoid errors.');
    }

    dbInfo.version = dbInfo.db.version;
  }

  return false;
};

var getConnection = function getConnection(_ref, forceUpgrade) {
  var name = _ref.name,
      version = _ref.version,
      db = _ref.db,
      storeName = _ref.storeName;

  return new Promise(function (resolve, reject) {
    var shouldClear = false;

    if (db && !forceUpgrade) {
      resolve(db);
      return;
    }

    if (db && forceUpgrade) {
      db.close();
    }

    try {
      var dbConnection = indexedDB$1.open(name, version);

      // An upgrade needs to be performed, clear out the old object stores
      // and then create the new object store
      dbConnection.onupgradeneeded = function (evt) {
        var oldVersion = evt.oldVersion,
            newVersion = evt.newVersion;

        var dbInstance = dbConnection.result;

        try {
          dbInstance.createObjectStore(storeName);
        } catch (err) {
          if (err.name === 'ConstraintError') {
            console.warn('Database ' + name + ' has been upgraded from ' + oldVersion + ' -> ' + newVersion + ' but the table ' + storeName + ' already exists.');

            // Force the store to clear because we've incremented the version
            shouldClear = true;
          } else {
            throw err;
          }
        }
      };

      // An error occured attempting to connect to the DB
      dbConnection.onerror = function (evt) {
        // See https://mzl.la/2wCPech
        evt.preventDefault();

        reject(evt.target.error);
      };

      // Successfully connected to the database
      dbConnection.onsuccess = function (evt) {
        var dbInstance = evt.target.result;

        if (shouldClear) {
          resolve(clearStore(dbInstance, storeName));
          return;
        }

        resolve(dbInstance);
        return;
      };
    } catch (e) {
      reject(e);
    }

    return;
  });
};

var initStorage = function initStorage(_ref2) {
  var dbName = _ref2.dbName,
      version = _ref2.version,
      storeName = _ref2.storeName,
      options = _ref2.options;

  // Get the context of the provided dbName
  var dbInfo = dbContexts[dbName];
  var opts = Object.assign({}, defaultOptions, options);

  // Or initialize one if it doesn't exist
  if (!dbInfo) {
    dbInfo = {
      db: null,
      name: dbName,
      version: version,
      storeName: storeName,
      options: opts
    };

    if (!options.disableContexts) {
      // Register the new context
      dbContexts[dbName] = dbInfo;
    }
  }

  // Get the database connection, if we need to upgrade it then perform the
  // required steps.
  return getConnection(dbInfo, false).then(function (db) {
    dbInfo.db = db;

    if (isUpgradeRequired(dbInfo)) {
      return getConnection(dbInfo, true);
    }

    return db;
  });
};

var destroyDatabase = function destroyDatabase(databaseName) {
  var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return new Promise(function (resolve, reject) {
    var _opts$enableLogging = opts.enableLogging,
        enableLogging = _opts$enableLogging === undefined ? false : _opts$enableLogging;


    var req = getIndexedDB().deleteDatabase(databaseName);

    req.onsuccess = function () {
      if (enableLogging) {
        console.info('Successfully removed IndexedDB: ', databaseName);
      }

      resolve(true);
    };
    req.onerror = function (evt) {
      if (enableLogging) {
        console.warn('Unable to delete IndexedDB: ', databaseName);
      }

      // See https://mzl.la/2wCPech
      evt.preventDefault();

      reject(evt.target.error);
    };
    req.onblocked = function (evt) {
      if (enableLogging) {
        console.warn('Blocked attempting to delete IndexedDB: ', databaseName);
      }

      reject(evt);
    };
  });
};

function validateKey(key) {
  if (typeof key !== 'string') {
    console.warn('Keys must be strings, converting ' + key + ' to a string.');
    return String(key);
  }

  return key;
}

var createIndexedDBDriver = function createIndexedDBDriver(dbInstance) {
  // Retrieve an item from the desired object store under the provided key
  var getItem = function getItem(name, key) {
    key = validateKey(key);

    return new Promise(function (resolve, reject) {
      var transaction = dbInstance.transaction(name, READ_ONLY);
      var objectStore = transaction.objectStore(name);

      var req = objectStore.get(key);

      req.onsuccess = function () {
        return resolve(req.result);
      };
      req.onabort = function () {
        return reject(transaction);
      };
      req.onerror = function () {
        return reject(transaction);
      };
    });
  };

  // Store an item in the provided store name under the provided key
  var setItem = function setItem(name, key, value) {
    key = validateKey(key);

    return new Promise(function (resolve, reject) {
      var transaction = dbInstance.transaction(name, READ_WRITE);
      var objectStore = transaction.objectStore(name);

      // Saving as `null` will result in a subsequent getItem returning `undefined`
      // Make it `undefined` immediately so that there is consistency
      if (value === null) {
        value = undefined;
      }

      objectStore.put(value, key);

      transaction.oncomplete = function () {
        return resolve(value, key);
      };
      transaction.onabort = function () {
        return reject(transaction);
      };
      transaction.onerror = function () {
        return reject(transaction);
      };
    });
  };

  // Return all the items stored in the provided store name
  var getCollection = function getCollection(name) {
    return new Promise(function (resolve, reject) {
      var transaction = dbInstance.transaction(name, READ_WRITE);
      var objectStore = transaction.objectStore(name);

      var req = objectStore.getAll();

      req.onsuccess = function () {
        return resolve(req.result);
      };
      req.onabort = function () {
        return reject(req);
      };
      req.onerror = function () {
        return reject(req);
      };
    });
  };

  // Remove an item from the provided store name under the provided key
  var removeItem = function removeItem(name, key) {
    key = validateKey(key);

    return new Promise(function (resolve, reject) {
      var transaction = dbInstance.transaction(name, READ_WRITE);
      var objectStore = transaction.objectStore(name);

      objectStore.delete(key);

      transaction.oncomplete = function () {
        return resolve();
      };
      transaction.onabort = function () {
        return reject(transaction);
      };
      transaction.onerror = function () {
        return reject(transaction);
      };
    });
  };

  var clear = function clear(name) {
    return new Promise(function (resolve, reject) {
      try {
        var transaction = dbInstance.transaction(name, READ_WRITE);
        var objectStore = transaction.objectStore(name);

        var req = objectStore.clear();

        var onError = function onError() {
          var err = req.error ? req.error : req.transaction.error;
          reject(err);
        };

        transaction.oncomplete = function () {
          return resolve();
        };
        transaction.onabort = onError;
        transaction.onerror = onError;
      } catch (err) {
        reject({
          storeName: name,
          error: err
        });
      }
    });
  };

  // Get the size of the provided store name
  var length = function length(name) {
    return new Promise(function (resolve, reject) {
      var transaction = dbInstance.transaction(name, READ_ONLY);
      var objectStore = transaction.objectStore(name);

      var req = objectStore.count();

      req.onsuccess = function () {
        return resolve(req.result);
      };
      req.onabort = function () {
        return reject(req);
      };
      req.onerror = function () {
        return reject(req);
      };
    });
  };

  // Return all of the keys in the object store
  var keys = function keys(name) {
    return new Promise(function (resolve, reject) {
      var transaction = dbInstance.transaction(name, READ_ONLY);
      var objectStore = transaction.objectStore(name);

      try {
        var req = objectStore.openCursor();
        var storeKeys = [];

        req.onsuccess = function () {
          var cursor = req.result;

          // If the cursor is empty, send back the keys
          if (!cursor) {
            resolve(storeKeys);
            return;
          }

          // Otherwise keep going
          storeKeys.push(cursor.key);
          cursor.continue();
        };

        req.onerror = function () {
          return reject(req.error);
        };
      } catch (e) {
        reject(e);
      }
    });
  };

  var iterate = function iterate(name, callback) {
    return new Promise(function (resolve, reject) {
      var transaction = dbInstance.transaction(name, READ_ONLY);
      var objectStore = transaction.objectStore(name);

      try {
        var req = objectStore.openCursor();

        req.onsuccess = function () {
          var cursor = req.result;

          // If the cursor is empty, send back the keys
          if (!cursor) {
            resolve();
            return;
          }

          // Otherwise keep going
          callback(cursor.value, cursor.key, cursor);
          cursor.continue();
        };

        req.onerror = function () {
          return reject(req.error);
        };
      } catch (e) {
        reject(e);
      }
    });
  };

  // Kills the connection to the DB
  var close = function close(dbName, options) {
    return new Promise(function (resolve) {
      dbInstance.close();

      if (options.enableLogging) {
        console.info('Closed connection to: ', dbName);
      }

      resolve();
    });
  };

  // Destroys the database instance
  var destroy = function destroy(dbName, options) {
    dbInstance.close();

    return destroyDatabase(dbName, options);
  };

  return {
    getItem: getItem,
    setItem: setItem,
    getCollection: getCollection,
    removeItem: removeItem,
    clear: clear,
    length: length,
    keys: keys,
    iterate: iterate,
    close: close,
    destroy: destroy
  };
};

var createMemoryDriver = function createMemoryDriver(storage) {
  var getItem = function getItem(storeName, key) {
    var store = storage[storeName];
    var value = store ? store[key] : undefined;

    return Promise.resolve(value);
  };

  var setItem = function setItem(storeName, key, value) {
    if (!storage[storeName]) {
      storage[storeName] = {};
    }
    var store = storage[storeName];
    store[key] = value;

    return Promise.resolve(value);
  };

  var clear = function clear(storeName) {
    storage[storeName] = {};

    
  };

  var close = function close() {
    return Promise.resolve();
  };

  var getCollection = function getCollection(storeName) {
    var store = storage[storeName] || {};

    return Object.keys(store).map(function (key) {
      return store[key];
    });
  };

  var removeItem = function removeItem(storeName, key) {
    delete storage[storeName][key];
  };

  var length = function length(storeName) {
    return Object.keys(storage[storeName]).length;
  };

  var keys = function keys(storeName) {
    return Object.keys(storage[storeName]);
  };

  var iterate = function iterate(storeName, callback) {
    var store = storage[storeName];
    return Object.keys(store).map(function (key) {
      return callback(key, store[key]);
    });
  };

  var destroy = function destroy() {
    storage = {};

    return true;
  };

  return {
    getItem: getItem,
    setItem: setItem,
    clear: clear,
    close: close,
    getCollection: getCollection,
    removeItem: removeItem,
    length: length,
    keys: keys,
    iterate: iterate,
    destroy: destroy
  };
};

var indexedDB = getIndexedDB();

function createInstance(_ref) {
  var storeName = _ref.storeName,
      _ref$version = _ref.version,
      version = _ref$version === undefined ? DEFAULT_VERSION : _ref$version,
      _ref$dbName = _ref.dbName,
      dbName = _ref$dbName === undefined ? DEFAULT_DB_NAME : _ref$dbName,
      _ref$options = _ref.options,
      options = _ref$options === undefined ? {} : _ref$options;

  var memoryStorage = {};

  if (!indexedDB) {
    throw new Error('No indexedDB implementation found!');
  }

  if (!storeName) {
    throw new Error('No store name provided for Indexed DB instance.');
  }

  if (!dbName) {
    throw new Error('No database name provided for Indexed DB instance.');
  }

  var databaseReadyPromise = initStorage({ dbName: dbName, storeName: storeName, version: version, options: options }).then(function (dbInstance) {
    return createIndexedDBDriver(dbInstance);
  }).catch(function (error) {
    var name = error.name;
    // `InvalidStateError` occurs in Firefox private mode.
    // `SecurityError` occurs in Safari.
    // When this happens we need to use an in-memory storage solution
    // instead of relying of IndexedDB.

    if (name && name === 'InvalidStateError' || name === 'SecurityError') {
      return createMemoryDriver(memoryStorage);
    }

    throw new Error(error);
  });

  var getItem = function getItem(key) {
    return databaseReadyPromise.then(function (dbInstance) {
      return dbInstance.getItem(storeName, key);
    });
  };

  var setItem = function setItem(key, value) {
    return databaseReadyPromise.then(function (dbInstance) {
      return dbInstance.setItem(storeName, key, value);
    });
  };

  var clear = function clear() {
    return databaseReadyPromise.then(function (dbInstance) {
      return dbInstance.clear(storeName);
    });
  };

  var close = function close() {
    return databaseReadyPromise.then(function (dbInstance) {
      return dbInstance.close(dbName, options);
    });
  };

  var getCollection = function getCollection() {
    return databaseReadyPromise.then(function (dbInstance) {
      return dbInstance.getCollection(storeName);
    });
  };

  var removeItem = function removeItem(key) {
    return databaseReadyPromise.then(function (dbInstance) {
      return dbInstance.removeItem(storeName, key);
    });
  };

  var length = function length() {
    return databaseReadyPromise.then(function (dbInstance) {
      return dbInstance.length(storeName);
    });
  };

  var keys = function keys() {
    return databaseReadyPromise.then(function (dbInstance) {
      return dbInstance.keys(storeName);
    });
  };

  var iterate = function iterate(callback) {
    return databaseReadyPromise.then(function (dbInstance) {
      return dbInstance.iterate(storeName, callback);
    });
  };

  var destroy = function destroy() {
    return databaseReadyPromise.then(function (dbInstance) {
      return dbInstance.destroy(dbName, options);
    });
  };

  return {
    getItem: getItem,
    setItem: setItem,
    clear: clear,
    close: close,
    getCollection: getCollection,
    removeItem: removeItem,
    length: length,
    keys: keys,
    iterate: iterate,
    destroy: destroy
  };
}

exports.createStorageInstance = createInstance;
exports.destroyDatabase = destroyDatabase;
exports.getIndexedDB = getIndexedDB;
